<template>
  <!--
    Thin bridge around the REAL DigPinkCampaignPageV2 component. By mounting
    the production component instead of reimplementing its layout, the preview
    cannot drift from what visitors actually see on /campaigns/{id}.

    The `preview` flag on DigPinkCampaignPageV2 disables the 30s view-tracking
    poll and initial captureView — otherwise loading the editor would log fake
    campaign views.
  -->
  <dig-pink-campaign-page-v2
    :campaign-id="campaignId"
    :pledge-id="null"
    :my-campaign="initialCampaign"
    :foundation="foundation"
    :foundation-template-content="content"
    :user="null"
    :preview="true"
  />
</template>

<script>
const EDITOR_SOURCE = 'template-editor';

export default {
  name: 'FoundationTemplatePreview',

  props: {
    campaignId: { type: [Number, String], required: true },
    initialCampaign: { type: Object, required: true },
    foundation: { type: Object, default: null },
    initialContent: { type: Object, required: true },
  },

  data() {
    return {
      // Reactive content: DigPinkCampaignPageV2 has a watcher that re-normalizes
      // when this prop changes, so every postMessage update flows through to
      // the real v2 components with zero duplication.
      content: { ...this.initialContent },
    };
  },

  mounted() {
    window.addEventListener('message', this.onMessage);
    // Handshake: tell the editor we're ready so it can push its current state
    // in case the iframe finished loading after the editor mounted.
    this.postToParent({ type: 'preview:ready' });
  },

  beforeUnmount() {
    window.removeEventListener('message', this.onMessage);
  },

  methods: {
    onMessage(event) {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.source !== EDITOR_SOURCE) return;

      if (data.type === 'content:update' && data.content) {
        this.content = data.content;
      } else if (data.type === 'section:scroll' && data.sectionKey) {
        this.scrollToSection(data.sectionKey);
      } else if (data.type === 'preview:open-modal') {
        this.openPreviewModal(data.modal, data.step);
      } else if (data.type === 'preview:close-modals') {
        this.closePreviewModals();
      }
    },

    /**
     * Open one of the preview-controllable modals via the global mitt
     * emitter (the same hooks the public CTAs use, so the preview is
     * exercising the real production path). When a step is provided
     * the support modal is also nudged to that step on the next tick —
     * support-modal:set-step waits for the modal to mount before
     * applying, so the order doesn't matter.
     */
    openPreviewModal(target, step) {
      if (target === 'support') {
        this.emitter?.emit('open-support-modal');
        if (typeof step === 'number') {
          this.emitter?.emit('support-modal:set-step', step);
        }
      } else if (target === 'donation') {
        this.emitter?.emit('open-donation-modal');
      }
    },

    closePreviewModals() {
      // Match the existing public-page conventions:
      //   donation widget closes via 'donation-modal-close'
      //   support modal exposes 'close-support-modal' for preview-driven close
      this.emitter?.emit('donation-modal-close');
      this.emitter?.emit('close-support-modal');
    },

    scrollToSection(key) {
      // V2 sub-components use known anchor IDs (#hero, #about, #impact,
      // #activity). Map editor tab keys to the closest anchor.
      const anchor = {
        hero: '#hero',
        founder_cta: '#hero',
        research_model: '#research',
        precision_analysis: '#research',
        support_onboarding: '#about',
        activity_section: '#activity',
        gallery: '#about',
        programs: 'footer',
        footer: 'footer',
      }[key];
      if (!anchor) return;
      const el = anchor.startsWith('#')
        ? document.querySelector(anchor)
        : document.querySelector(anchor);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    postToParent(payload) {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          { source: 'template-preview', ...payload },
          window.location.origin
        );
      }
    },
  },
};
</script>
